import config from "config";
import ko from "knockout";
import _ from "lodash";
import authService from "services/authService";
import embeddingService from "services/embeddingService";
import navHelper from "services/navHelper";
import qualityAuth from "services/qualityAuth";
import toasts from "services/toastService";
import userService from "services/userService";
import errorCallbackFactory from "util/errorCallbackFactory";
import ViewModel from "viewModels/ViewModel";

/**
 * A ViewModel to keep coaching logic seperate.
 *
 */
const CoachingView = ViewModel.extend(function (conversationId, isActive) {
    const self = this;

    self.conversationId = conversationId;
    const baseUrl = embeddingService.getParentHref().split("/#")[0];
    self.conversationIds = self.computed(function () {
        const hash = navHelper.buildInteractionHash(
            "admin",
            self.conversationId()
        );
        return [`${baseUrl}/${hash}`];
    });
    self.noUrlConversationIds = self.computed(function () {
        return [self.conversationId()];
    });

    self.participants = ko.observableArray();

    self.actionLabel = ko.i18n.renderString("playback.scheduleCoaching");

    self.newApptLabel = ko.i18n.renderString("playback.newAppointment");

    self.addtoExistingLabel = ko.i18n.renderString("playback.addAllToExisting");

    self.agents = self.computed(function () {
        if (_.isEmpty(self.participants())) {
            return [];
        }
        const participants = self
            .participants()
            .filter((participant) => participant.purpose === "agent");

        const agents = _.map(participants, function (participant) {
            return {
                id: participant.userId,
                name: participant.name,
            };
        });

        return _.uniqBy(agents, "id");
    });

    self.isActiveInteraction = isActive;

    self.agentDetails = ko.observableArray();

    self.canScheduleCoaching = self.computed(function () {
        let divisionIds;
        const agentDetails = self.agentDetails();
        divisionIds = _.map(agentDetails, function (a) {
            return a.division().id();
        });

        return (
            self.agents() &&
            self.agents().length > 0 &&
            qualityAuth.canScheduleCoaching(divisionIds) &&
            !self.isActiveInteraction()
        );
    });

    self.showModal = ko.observable(false);

    self.showCoachingModal = function () {
        const modalRequest = {
            modalId: "wemCoachingUi",
            modalData: {
                attachments: { interactionIds: [self.conversationId()] },
                agents: self.agents(),
            },
        };
        if (ko.i18n.getTimezone()) {
            modalRequest.modalData.timeZone = { id: ko.i18n.getTimezone() };
        }
        embeddingService.requestModal(modalRequest);
    };

    self.showCoachingModalAddAllToExisting = function () {
        const agents = self.agentDetails().map((a) => {
            return { id: a.id, name: a.name() };
        });

        const modalRequest = {
            modalId: "wemCoachingUiAddExisting",
            modalData: {
                agents: agents,
                interactionId: self.conversationId(),
            },
        };
        if (ko.i18n.getTimezone()) {
            modalRequest.modalData.timeZone = { id: ko.i18n.getTimezone() };
        }
        embeddingService.requestModal(modalRequest);
    };

    self.currentUser = self.computed(function () {
        return userService.currentUser();
    });

    // Need to use the window location to build an absolute path if it's not absolute already
    if (config.api.uri.startsWith("http")) {
        self.apiRoot = config.api.uri;
    } else {
        self.apiRoot = window.location.origin + config.api.uri;
    }

    self.modalClosed = function (event, data) {
        const wemModal = data.srcElement;
        self.showModal(false);
        wemModal.resetModal();
    };

    self.saved = function (event, data) {
        const wemModal = data.srcElement;
        const res = data.detail;

        // If Response errored out, show a manual toast regardless of toggle
        if (res.status === 409) {
            // clash
            toasts.notifyMinorAlert(
                "playback.coaching.appointmentClashToast.title",
                {
                    bodyMessageKey:
                        "playback.coaching.appointmentClashToast.body",
                }
            );
            // Fall back generic
        } else if (res.status !== 201 && res.status !== 202) {
            toasts.notifyMinorAlert(
                "playback.coaching.appointmentGenericErrorToast.title",
                {
                    bodyMessageKey:
                        "playback.coaching.appointmentGenericErrorToast.body",
                }
            );
        } else if (res.status === 202) {
            self.showModal(false);
            wemModal.resetModal();
            wemModal.toggleModal(false);
        }
    };

    self.permissions = ko.observableArray();
    self.permissionsPromise = self.computed(function () {
        return authService.getRawPermissions().then((rawPerms) => {
            self.permissions(rawPerms);
        });
    });

    self.subscribe(self.agents, function () {
        if (self.agents().length === 0) {
            return;
        }

        const agentIds = _.map(self.agents(), function (a) {
            return a.id;
        });

        const param = { id: agentIds };

        return userService
            .getUsers(param)
            .then((res) => {
                res().forEach((r) => {
                    if (r.division && r.division().id) {
                        const agentDivision = r.division().id();
                        const hasPermissionForAgent =
                            qualityAuth.canScheduleCoaching([agentDivision]);
                        if (hasPermissionForAgent) {
                            self.agentDetails.push(r);
                        }
                    }
                });
            })
            .catch(() => {
                errorCallbackFactory.createCallback({
                    namespace: "coaching.get-users-error",
                });
            });
    });
});

export default CoachingView;
